import React from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { connect } from 'react-redux';
import { DARK_MODE, JLR_PLANT } from '../../constants';

// Enhanced JLR Button Component with luxury styling
const StyledJlrButton = styled(Button)(({ 
  theme, 
  themeMode, 
  plantName, 
  variant = 'primary', 
  customColor,
  size = 'medium'
}) => {
  const isJlr = plantName === JLR_PLANT;
  const isDarkMode = themeMode === DARK_MODE;
  
  // Size configurations
  const sizeConfig = {
    small: {
      padding: isJlr ? '8px 16px' : '6px 12px',
      fontSize: isJlr ? '0.875rem' : '0.75rem',
      minHeight: isJlr ? '36px' : '32px',
      minWidth: isJlr ? '100px' : '80px',
    },
    medium: {
      padding: isJlr ? '13px 20px' : '8px 16px',
      fontSize: isJlr ? '24px' : '0.875rem',
      minHeight: isJlr ? '56px' : '36px',
      minWidth: isJlr ? '346px' : '120px',
      width: isJlr ? '346px' : 'auto',
    },
    large: {
      padding: isJlr ? '13px 20px' : '12px 24px',
      fontSize: isJlr ? '24px' : '1rem',
      minHeight: isJlr ? '56px' : '44px',
      minWidth: isJlr ? '346px' : '160px',
      width: isJlr ? '346px' : 'auto',
    },
  };

  const currentSize = sizeConfig[size] || sizeConfig.medium;
  
  const baseStyles = {
    fontFamily: isJlr ? "'JLR Emeric', sans-serif" : theme.typography.fontFamily,
    fontWeight: isJlr ? 600 : 500,
    borderRadius: isJlr ? '20px' : '8px',
    textTransform: 'none',
    letterSpacing: isJlr ? '0.5px' : '0.08em',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: isJlr 
      ? (isDarkMode 
        ? '0 4px 20px rgba(0, 0, 0, 0.3)'
        : '0 4px 20px rgba(0, 0, 0, 0.1)')
      : 'none',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '10px',
    isolation: 'isolate',
    ...currentSize,
    
    // Shimmer effect for JLR
    ...(isJlr && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: '-100%',
        width: '100%',
        height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
        transition: 'left 0.6s ease',
      },
      '&:hover::before': {
        left: '100%',
      },
    }),
    
    '&:hover': {
      transform: isJlr ? 'translateY(-2px) scale(1.02)' : 'translateY(-1px)',
      boxShadow: isJlr 
        ? (isDarkMode 
          ? '0 8px 40px rgba(0, 0, 0, 0.4)'
          : '0 8px 40px rgba(0, 0, 0, 0.2)')
        : '0 4px 8px rgba(0, 0, 0, 0.1)',
    },
    
    '&:active': {
      transform: isJlr ? 'translateY(0) scale(1)' : 'translateY(0)',
    },
    
    '&.Mui-disabled': {
      opacity: 0.6,
      transform: 'none',
      boxShadow: 'none',
      background: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      color: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
    },
  };

  // Handle custom colors (for backward compatibility)
  if (customColor) {
    return {
      ...baseStyles,
      background: customColor,
      color: '#ffffff',
      border: 'none',
      '&:hover': {
        ...baseStyles['&:hover'],
        background: customColor,
        filter: 'brightness(1.1)',
      },
      '&.Mui-disabled': {
        ...baseStyles['&.Mui-disabled'],
        background: customColor,
        opacity: 0.6,
      },
    };
  }

  // Variant-based styling
  switch (variant) {
    case 'primary':
      return {
        ...baseStyles,
        background: isJlr 
          ? (isDarkMode 
            ? '#1B681B'
            : 'linear-gradient(90deg, #231F20 0%, #000000 100%)')
          : theme.palette.primary.main,
        color: '#ffffff',
        border: 'none',
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? (isDarkMode 
              ? '#1a5a1a'
              : 'linear-gradient(90deg, #1a1a1a 0%, #000000 100%)')
            : theme.palette.primary.dark,
        },
      };

    case 'secondary':
      return {
        ...baseStyles,
        background: isJlr 
          ? (isDarkMode 
            ? '#2B2E33'
            : 'linear-gradient(135deg, #c8b99c, #9d8b6f)')
          : theme.palette.secondary.main,
        color: isJlr ? (isDarkMode ? '#E1E1E1' : '#2d4a2d') : '#ffffff',
        border: isJlr ? `1px solid ${isDarkMode ? '#3D4B3D' : '#c8b99c'}` : 'none',
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? (isDarkMode 
              ? '#3a3d42'
              : 'linear-gradient(135deg, #9d8b6f, #8a7a5d)')
            : theme.palette.secondary.dark,
        },
      };

    case 'success':
      return {
        ...baseStyles,
        background: isJlr 
          ? 'linear-gradient(135deg, #27ae60, #2ecc71)'
          : '#4caf50',
        color: '#ffffff',
        border: 'none',
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? 'linear-gradient(135deg, #219a52, #27ae60)'
            : '#45a049',
        },
      };

    case 'warning':
      return {
        ...baseStyles,
        background: isJlr 
          ? 'linear-gradient(135deg, #f39c12, #e67e22)'
          : '#ff9800',
        color: '#ffffff',
        border: 'none',
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? 'linear-gradient(135deg, #e67e22, #d35400)'
            : '#f57c00',
        },
      };

    case 'error':
      return {
        ...baseStyles,
        background: isJlr 
          ? 'linear-gradient(135deg, #e74c3c, #c0392b)'
          : '#f44336',
        color: '#ffffff',
        border: 'none',
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? 'linear-gradient(135deg, #c0392b, #a93226)'
            : '#d32f2f',
        },
      };

    case 'outlined':
    default:
      return {
        ...baseStyles,
        background: 'transparent',
        color: isJlr ? (isDarkMode ? '#ffffff' : '#2d4a2d') : theme.palette.text.primary,
        border: `1px solid ${isJlr 
          ? (isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(45, 74, 45, 0.2)')
          : theme.palette.divider}`,
        '&:hover': {
          ...baseStyles['&:hover'],
          background: isJlr 
            ? (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(45, 74, 45, 0.05)')
            : 'rgba(0, 0, 0, 0.04)',
        },
      };
  }
});

const JlrButton = ({
  children,
  variant = 'primary',
  size = 'medium',
  customColor,
  backgroundColor, // For backward compatibility
  name, // For backward compatibility
  handleButtonClick, // For backward compatibility
  onClick,
  themeMode,
  plantName,
  ...props
}) => {
  // Handle backward compatibility
  const buttonText = children || name;
  const clickHandler = onClick || handleButtonClick;
  const finalCustomColor = customColor || backgroundColor;

  return (
    <StyledJlrButton
      themeMode={themeMode}
      plantName={plantName}
      variant={variant}
      size={size}
      customColor={finalCustomColor}
      onClick={clickHandler}
      {...props}
    >
      {buttonText}
    </StyledJlrButton>
  );
};

JlrButton.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'outlined']),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  customColor: PropTypes.string,
  backgroundColor: PropTypes.string, // Legacy prop
  name: PropTypes.string, // Legacy prop
  handleButtonClick: PropTypes.func, // Legacy prop
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  themeMode: PropTypes.string,
  plantName: PropTypes.string,
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.themeMode,
  plantName: state.user.plant,
});

export default connect(mapStateToProps)(JlrButton);
